/** Shared model choices and plan defaults for Agent Chat and its paywalls. */
import type {
  PlanTier,
  SubscriptionSnapshot,
} from "../../types/src/subscription";

export type AgentModelProvider = "google" | "anthropic" | "openai";
export type AgentModelBrand = "gemini" | "claude" | "openai";
export type AgentModelTier = "fast" | "balanced" | "pro";
export type AgentModelMinimumTier = PlanTier;

export interface AgentModelEntry {
  id: string;
  runtimeModelId: string;
  displayName: string;
  brand: AgentModelBrand;
  provider: AgentModelProvider;
  tier: AgentModelTier;
  minimumTier: AgentModelMinimumTier;
  slug: string;
  isNew?: boolean;
}

export const AGENT_MODEL_AUTO = "auto";

/** Task-specific extraction keeps its own default; Auto has a separate role. */
export const AGENT_MODEL_RUNTIME_IDS = {
  geminiFree: "google/gemini-3.5-flash-lite",
  geminiFlashLite: "google/gemini-3.5-flash-lite",
  geminiFlash: "google/gemini-3.6-flash",
  geminiPlus: "google/gemini-3.7-flash",
  geminiPro: "google/gemini-3.8-flash",
} as const;

/** Retired Gemini selections migrate without changing saved Claude choices. */
const LEGACY_AGENT_MODEL_SELECTIONS: Readonly<Record<string, string>> = {
  "google/gemini-2.5-flash-lite": AGENT_MODEL_AUTO,
  "google/gemini-3.1-flash-lite": AGENT_MODEL_AUTO,
  "google/gemini-3.5-flash-lite": AGENT_MODEL_AUTO,
  "google/gemini-3.6-flash": AGENT_MODEL_AUTO,
  "google/gemini-3.1-pro-preview": AGENT_MODEL_RUNTIME_IDS.geminiPlus,
  "google/gemini-3.5-flash": AGENT_MODEL_RUNTIME_IDS.geminiPlus,
};

export const AGENT_MODEL_CATALOG: readonly AgentModelEntry[] = [
  {
    id: AGENT_MODEL_AUTO,
    runtimeModelId: AGENT_MODEL_RUNTIME_IDS.geminiFree,
    displayName: "Auto",
    brand: "gemini",
    provider: "google",
    tier: "fast",
    minimumTier: "free",
    slug: "auto",
  },
  {
    id: AGENT_MODEL_RUNTIME_IDS.geminiPlus,
    runtimeModelId: AGENT_MODEL_RUNTIME_IDS.geminiPlus,
    displayName: "Gemini 3.7 Flash",
    brand: "gemini",
    provider: "google",
    tier: "pro",
    minimumTier: "plus",
    // Reuse the installed clients' translation key for a Flash model.
    slug: "geminiFlash",
  },
  {
    id: AGENT_MODEL_RUNTIME_IDS.geminiPro,
    runtimeModelId: AGENT_MODEL_RUNTIME_IDS.geminiPro,
    displayName: "Gemini 3.8 Flash",
    brand: "gemini",
    provider: "google",
    tier: "pro",
    minimumTier: "pro",
    slug: "geminiPro",
    isNew: true,
  },
  // Product display choices intentionally retain their existing Claude names.
  // Execution remains on Gemini until the separately approved provider change.
  {
    id: "anthropic/claude-sonnet-5",
    runtimeModelId: AGENT_MODEL_RUNTIME_IDS.geminiPlus,
    displayName: "Claude Sonnet 5",
    brand: "claude",
    provider: "anthropic",
    tier: "pro",
    minimumTier: "plus",
    slug: "claudeSonnet",
  },
  {
    id: "anthropic/claude-opus-5",
    runtimeModelId: AGENT_MODEL_RUNTIME_IDS.geminiPro,
    displayName: "Claude Opus 5",
    brand: "claude",
    provider: "anthropic",
    tier: "pro",
    minimumTier: "pro",
    slug: "claudeOpus",
  },
];

export const AGENT_MODEL_PROVIDER_ORDER: readonly AgentModelProvider[] = [
  "google",
  "anthropic",
  "openai",
];

export function findAgentModel(id: string | null | undefined) {
  if (!id) return undefined;
  const canonicalId = LEGACY_AGENT_MODEL_SELECTIONS[id] ?? id;
  return AGENT_MODEL_CATALOG.find((entry) => entry.id === canonicalId);
}

export function normalizeAgentModelSelection(id: string | null | undefined) {
  return findAgentModel(id)?.id;
}

export function supportsGeminiThinkingBudget(modelId: string) {
  return /^google\/gemini-2(?:[.-]|$)/.test(modelId);
}

/** Paid access requires both the verified policy and the effective seat tier. */
export function getAgentModelPlanTier(
  snapshot?: Pick<SubscriptionSnapshot, "modelPolicy" | "tier" | "seat"> | null,
): PlanTier {
  if (snapshot?.modelPolicy !== "sonnet_plus_opus_auto") return "free";
  return snapshot.seat?.tier ?? snapshot.tier ?? "free";
}

export function getDefaultAgentModelForPlan(tier: PlanTier) {
  const id =
    tier === "pro"
      ? AGENT_MODEL_RUNTIME_IDS.geminiPro
      : tier === "plus"
        ? AGENT_MODEL_RUNTIME_IDS.geminiPlus
        : AGENT_MODEL_AUTO;
  return findAgentModel(id)!;
}

/** Paywall headlines are a product display choice, separate from Auto routing. */
export function getPaywallAgentModelForPlan(tier: PlanTier) {
  return tier === "pro"
    ? findAgentModel("anthropic/claude-opus-5")!
    : getDefaultAgentModelForPlan(tier);
}

export function isProAgentModel(id: string | null | undefined) {
  return findAgentModel(id)?.tier === "pro";
}

export function agentModelMinimumTier(
  id: string | null | undefined,
): AgentModelMinimumTier {
  return findAgentModel(id)?.minimumTier ?? "free";
}

export function isAgentModelSelection(id: string | null | undefined) {
  return Boolean(findAgentModel(id));
}

export function agentModelDescriptionKey(slug: string) {
  return slug === "auto"
    ? "chat.model.auto.description"
    : `chat.model.options.${slug}.description`;
}
